import { useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import {
  Bell,
  HardDrive,
  CreditCard,
  Crown,
  FolderTree,
  Image,
  LogOut,
  Megaphone,
  Mic2,
  Package,
  PackageMinus,
  LayoutDashboard,
  ShoppingCart,
  FileSpreadsheet,
  Store,
  CalendarDays,
  MessageSquare,
  Ticket,
  Trophy,
  ChevronDown,
  Search,
  ScrollText,
  Users,
  HelpCircle,
  ShieldCheck,
  UserCog,
  UserCircle,
  Coins,
  Wallet,
  Download,
  KeyRound,
  Gift,
  ArrowLeftRight,
  Video,
  Truck,
  ClipboardList,
  Receipt,
  Settings,
  FileText,
  Building2,
  HandCoins,
  Gauge,
  TrendingUp,
  type LucideIcon,
  BarChart3,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/lib/i18n';
import { Button, ScrollArea } from '@/components/ui';
import type { AdminRole } from '@/lib/api-admin';
import { useAuthContext, useHasRole } from '@/providers/AuthProvider';
import { isMenuAllowed, menusOf } from '@/lib/menuAccess';
import { VENDOR_ROUTE_ROLES } from '@/features/vendor/lib/vendorActionRbac';
import { FUNDING_ROUTE_ROLES } from '@/features/funding/lib/fundingActionRbac';
import { PRODUCT_EXPORT_JOB_ROLES } from '@/features/catalog/exportJobs/productExportJobs';
import { POPULAR_SEARCH_MENU_ENABLED } from '@/features/search/visibility';

interface NavEntry {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  required: ReadonlyArray<AdminRole>;
  indent?: boolean;
  /** When set, active-state matches the full pathname+search (for query-param deep links like /policies?type=TERMS). */
  matchSearch?: boolean;
}

interface NavSubGroup {
  type: 'subgroup';
  labelKey: string;
  icon: LucideIcon;
  required: ReadonlyArray<AdminRole>;
  prefixes: ReadonlyArray<string>;
  entries: ReadonlyArray<NavEntry>;
}

type NavItem = NavEntry | NavSubGroup;

interface NavGroup {
  headingKey: string;
  items: ReadonlyArray<NavItem>;
}

const ALL_ROLES: ReadonlyArray<AdminRole> = [
  'SUPER_ADMIN',
  'OPS_ADMIN',
  'CS_AGENT',
  'MD_MANAGER',
  'FULFILLMENT_AGENT',
  'PICKUP_AGENT',
  'SETTLEMENT_AGENT',
  'AUDITOR',
];

const PRODUCT_ROLES: ReadonlyArray<AdminRole> = ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN'];
const MILEAGE_NAV_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT', 'AUDITOR'];

// 정책/벤치 RBAC 묶음 (재사용).
const POLICY_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'MD_MANAGER'];
const CONTENT_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'MD_MANAGER'];
const ORDER_ROLES: ReadonlyArray<AdminRole> = ['OPS_ADMIN', 'CS_AGENT', 'FULFILLMENT_AGENT', 'PICKUP_AGENT', 'SUPER_ADMIN'];

/**
 * 7그룹 재편 (task #18): 운영/정책 분리.
 * 상품 · 프로모션·할인 · 전시·콘텐츠 · 주문·배송 · 회원 · 알림 · 기본설정.
 * 라우트 경로/아이콘/RBAC 는 모두 기존 그대로 — 그룹핑/헤딩만 변경.
 */
/** QA-488 통계 — 매출 집계와 같은 역할 + 상품 담당. */
const STATS_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'AUDITOR', 'MD_MANAGER'];

const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  // 대시보드 최상단 (단독 그룹, 헤딩 없이 main 그룹에 단일 항목).
  {
    headingKey: 'sidebar.groups.main',
    items: [
      { to: '/dashboard', labelKey: 'sidebar.dashboard', icon: LayoutDashboard, required: ALL_ROLES },
    ],
  },
  // QA-488 통계(임시) — 하위 메뉴는 외부 임시 통계 페이지(iframe)가 관리한다.
  {
    headingKey: 'sidebar.groups.stats',
    items: [
      { to: '/stats', labelKey: 'sidebar.stats', icon: BarChart3, required: STATS_ROLES },
    ],
  },
  // 1. 상품 — 목록/카테고리/아티스트/펀딩.
  {
    headingKey: 'sidebar.groups.products',
    items: [
      { to: '/products',            labelKey: 'sidebar.products',        icon: Package,    required: PRODUCT_ROLES },
      { to: '/products/categories', labelKey: 'sidebar.categories',      icon: FolderTree, required: PRODUCT_ROLES },
      { to: '/products/notice-spec-templates', labelKey: 'sidebar.noticeSpecTemplates', icon: FileText, required: PRODUCT_ROLES },
      // QA-563 추가입력·동의 템플릿(4몰) — 상품 폼 [템플릿 불러오기]에서 쓴다.
      { to: '/products/collection-templates', labelKey: 'sidebar.collectionTemplates', icon: FileText, required: PRODUCT_ROLES },
      // QA-507 상품 공통 동의 4종의 몰별 문구.
      { to: '/products/consent-templates', labelKey: 'sidebar.consentTemplates', icon: FileText, required: ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN', 'AUDITOR'] },
      { to: '/products/web-ftp',    labelKey: 'sidebar.webFtp',          icon: HardDrive,  required: PRODUCT_ROLES },
      // 상품 엑셀 다운로드 내역 — 권한은 상품 엑셀 BE 와 같음(MD_MANAGER·SUPER_ADMIN·AUDITOR).
      { to: '/products/exports',    labelKey: 'sidebar.productExportJobs', icon: Download, required: PRODUCT_EXPORT_JOB_ROLES },
      { to: '/artists',             labelKey: 'sidebar.artists',         icon: Mic2,       required: ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN'] },
    ],
  },
  // 1.5 입점·펀딩 [오픈 r3] — 소속사 입점신청 심사 + 펀딩 캠페인.
  // 라우트/페이지는 기존 존재(vendor=빌드·라우트됨, funding=리다이렉트 해제). 메뉴 노출만 신규.
  {
    headingKey: 'sidebar.groups.partnerships',
    items: [
      { to: '/vendors',            labelKey: 'sidebar.vendorApplications', icon: Building2,  required: VENDOR_ROUTE_ROLES },
      { to: '/funding-campaigns',  labelKey: 'sidebar.fundingCampaigns',   icon: HandCoins,  required: FUNDING_ROUTE_ROLES },
      { to: '/funding-dashboard',  labelKey: 'sidebar.fundingDashboard',   icon: Gauge,      required: FUNDING_ROUTE_ROLES },
      // #2 정산 상품별 매출 집계 — 엔드포인트 RBAC=SUPER/OPS/AUDITOR.
      { to: '/settlements/sales-aggregation', labelKey: 'sidebar.salesAggregation', icon: TrendingUp, required: ['SUPER_ADMIN', 'OPS_ADMIN', 'AUDITOR'] },
    ],
  },
  // 2. 프로모션·할인 [신설] — 쿠폰/수량할인.
  {
    headingKey: 'sidebar.groups.promotion',
    items: [
      { to: '/coupons',                    labelKey: 'sidebar.coupons',          icon: Ticket, required: ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN'] },
      { to: '/products/quantity-discount', labelKey: 'sidebar.quantityDiscount', icon: Coins,  required: PRODUCT_ROLES },
    ],
  },
  // 3. 전시·콘텐츠 — 기존 CMS + 인기검색어.
  {
    headingKey: 'sidebar.groups.content',
    items: [
      { to: '/cms/main-page',      labelKey: 'sidebar.mainPage',      icon: Image,        required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      { to: '/cms/offline-stores', labelKey: 'sidebar.offlineStores', icon: Store,        required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      // 홈 화면 모달 팝업. 아래 '/cms/popups'(오프라인 팝업스토어)와 다른 도메인이라 라벨로 구분한다.
      { to: '/cms/home-popups',    labelKey: 'sidebar.homePopups',    icon: MessageSquare, required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      { to: '/cms/popups',         labelKey: 'sidebar.popups',        icon: CalendarDays, required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      { to: '/cms/events',         labelKey: 'sidebar.events',        icon: Trophy,       required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      // QA-407 — 이벤트 구분 없는 당첨자 발표 전체 목록(이관 글은 숨김 캠페인 1개에 모여 있어 이벤트에서 찾기 어렵다).
      { to: '/cms/event-winners',  labelKey: 'sidebar.eventWinners',  icon: Trophy,       required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      { to: '/cms/notices',        labelKey: 'sidebar.notices',       icon: Megaphone,    required: CONTENT_ROLES },
      {
        type: 'subgroup',
        labelKey: 'sidebar.everTv',
        icon: Video,
        required: CONTENT_ROLES,
        prefixes: ['/cms/ever-tv'],
        entries: [
          { to: '/cms/ever-tv',                   labelKey: 'sidebar.everTvVideos',   icon: Video, required: CONTENT_ROLES, indent: true },
          { to: '/cms/ever-tv/external-services', labelKey: 'sidebar.everTvExternal', icon: Video, required: CONTENT_ROLES, indent: true },
        ],
      },
      {
        type: 'subgroup',
        labelKey: 'sidebar.groups.faq',
        icon: HelpCircle,
        required: CONTENT_ROLES,
        prefixes: ['/faq'],
        entries: [
          { to: '/faq/categories', labelKey: 'sidebar.faqCategories', icon: HelpCircle, required: CONTENT_ROLES, indent: true },
          { to: '/faq/items',      labelKey: 'sidebar.faqItems',      icon: HelpCircle, required: CONTENT_ROLES, indent: true },
        ],
      },
      // QA-360: 인기 검색어 메뉴 숨김(코드·데이터는 유지).
      ...(POPULAR_SEARCH_MENU_ENABLED
        ? [{ to: '/search/popular', labelKey: 'sidebar.searchPopular', icon: Search, required: ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN', 'AUDITOR'] } as const]
        : []),
    ],
  },
  // 4. 주문·배송 — 주문/송장/결제/클레임/해외배송비.
  {
    headingKey: 'sidebar.groups.orders',
    items: [
      {
        type: 'subgroup',
        labelKey: 'sidebar.orders',
        icon: ShoppingCart,
        required: ORDER_ROLES,
        prefixes: ['/orders'],
        entries: [
          { to: '/orders',              labelKey: 'sidebar.orders',      icon: ShoppingCart,    required: ORDER_ROLES, indent: true },
          { to: '/orders/invoice-bulk', labelKey: 'sidebar.invoiceBulk', icon: FileSpreadsheet, required: ['OPS_ADMIN', 'FULFILLMENT_AGENT', 'SUPER_ADMIN'], indent: true },
          { to: '/orders/exports',      labelKey: 'sidebar.orderExportJobs', icon: Download,     required: ['OPS_ADMIN', 'SUPER_ADMIN'], indent: true },
          { to: '/orders/export-templates', labelKey: 'sidebar.orderExportTemplates', icon: Download, required: ['OPS_ADMIN', 'SUPER_ADMIN'], indent: true },
          { to: '/orders/manual',       labelKey: 'sidebar.manualOrder', icon: ClipboardList,   required: ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT'], indent: true },
          { to: '/orders/tax-invoices', labelKey: 'sidebar.taxInvoices', icon: Receipt,        required: ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT'], indent: true },
          { to: '/orders/cash-receipts', labelKey: 'sidebar.cashReceipts', icon: Receipt,       required: ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT'], indent: true },
        ],
      },
      // QA-613 결제 관리 하위로 — 주문 관리처럼 [결제 관리] 묶음 아래 2단계로 보인다.
      {
        type: 'subgroup',
        labelKey: 'sidebar.payments',
        icon: CreditCard,
        required: ['OPS_ADMIN', 'CS_AGENT', 'SETTLEMENT_AGENT', 'SUPER_ADMIN'],
        prefixes: ['/payments'],
        entries: [
          { to: '/payments',         labelKey: 'sidebar.payments',   icon: CreditCard, required: ['OPS_ADMIN', 'CS_AGENT', 'SETTLEMENT_AGENT', 'SUPER_ADMIN'], indent: true },
          // QA-558 환불 리스트 — 승인돼 환불을 기다리는 클레임(환불접수)만 모아 PG 취소·수기 환불.
          { to: '/payments/refunds', labelKey: 'sidebar.refundList', icon: CreditCard, required: ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'], indent: true },
        ],
      },
      { to: '/claims',                     labelKey: 'sidebar.claims',           icon: PackageMinus, required: ['CS_AGENT', 'OPS_ADMIN', 'SUPER_ADMIN'] },
      // 배송 관리 [통합] — 국내 배송비조건 + 해외 배송비 설정을 한 페이지 탭으로 일원화.
      { to: '/products/overseas-shipping', labelKey: 'sidebar.shippingManagement', icon: Truck,       required: PRODUCT_ROLES },
    ],
  },
  // 5. 회원 — 회원/등급/마일리지/예치금. (적립정책은 기본설정으로 이동)
  {
    headingKey: 'sidebar.groups.members',
    items: [
      { to: '/users/members',  labelKey: 'sidebar.members',         icon: Users,     required: ['SUPER_ADMIN', 'OPS_ADMIN', 'AUDITOR'] },
      { to: '/users/business', labelKey: 'sidebar.businessMembers',  icon: Building2, required: ['SUPER_ADMIN', 'OPS_ADMIN', 'AUDITOR'] },
      { to: '/users/grades',   labelKey: 'sidebar.userGrades',       icon: Crown,     required: ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN'] },
      { to: '/mileage',       labelKey: 'sidebar.mileageAdmin', icon: Coins,  required: MILEAGE_NAV_ROLES },
      { to: '/deposits',      labelKey: 'sidebar.depositAdmin', icon: Wallet, required: MILEAGE_NAV_ROLES },
    ],
  },
  // 6. 알림 [독립] — 단일 '메시지(알림톡)' 5탭 페이지로 통합(광고성/자동알림/템플릿/발송내역/연동).
  {
    headingKey: 'sidebar.groups.notifications',
    items: [
      { to: '/notifications', labelKey: 'sidebar.notificationMessage', icon: Bell, required: ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT', 'AUDITOR'] },
    ],
  },
  // 7. 기본설정 [정책통합] — 약관·정책/적립정책/환율/시스템.
  {
    headingKey: 'sidebar.groups.settings',
    items: [
      {
        type: 'subgroup',
        labelKey: 'sidebar.policies',
        icon: ScrollText,
        required: POLICY_ROLES,
        prefixes: ['/policies'],
        entries: [
          { to: '/policies',                   labelKey: 'sidebar.policyAll',         icon: ScrollText, required: POLICY_ROLES, indent: true, matchSearch: true },
          { to: '/policies?type=GUIDE',        labelKey: 'sidebar.policyGuide',       icon: ScrollText, required: POLICY_ROLES, indent: true, matchSearch: true },
          { to: '/policies?type=TERMS',        labelKey: 'sidebar.policyTerms',       icon: ScrollText, required: POLICY_ROLES, indent: true, matchSearch: true },
          { to: '/policies?type=PRIVACY',      labelKey: 'sidebar.policyPrivacy',     icon: ScrollText, required: POLICY_ROLES, indent: true, matchSearch: true },
          { to: '/policies?type=EMAIL_REJECT', labelKey: 'sidebar.policyEmailReject', icon: ScrollText, required: POLICY_ROLES, indent: true, matchSearch: true },
        ],
      },
      { to: '/mileage/earn-policies',   labelKey: 'sidebar.mileageEarnPolicies', icon: Gift,           required: MILEAGE_NAV_ROLES },
      { to: '/settings/exchange-rates', labelKey: 'sidebar.exchangeRates',       icon: ArrowLeftRight, required: POLICY_ROLES },
      { to: '/settings/order-policy',   labelKey: 'sidebar.orderPolicy',         icon: Settings,      required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      // QA-510 배송 업체(택배사) 관리 — 조회는 주문·출고 담당까지.
      { to: '/settings/couriers',       labelKey: 'sidebar.couriers',            icon: Truck,         required: ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT', 'FULFILLMENT_AGENT', 'MD_MANAGER', 'AUDITOR'] },
      { to: '/settings/shopping-policy', labelKey: 'sidebar.shoppingPolicy',      icon: ShoppingCart,  required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      { to: '/settings/order-form-fields', labelKey: 'sidebar.orderFormFields',   icon: FileText,      required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      { to: '/settings/bank-accounts',  labelKey: 'sidebar.bankAccounts',        icon: Wallet,        required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      // 매출 수집 Open API 제휴사(키·허용 IP·사용기간). 조회 OPS_ADMIN 포함, 쓰기는 화면에서 SUPER_ADMIN 만.
      { to: '/settings/open-api-partners', labelKey: 'sidebar.openApiPartners', icon: KeyRound,    required: ['SUPER_ADMIN', 'OPS_ADMIN'] },
      {
        // 시스템 서브그룹 — 관리자계정/권한은 admin 롤 self-gate, 내 계정(/admin/me)은 전원.
        // 서브그룹 required=ALL_ROLES 라야 비-admin 도 /admin/me 에 도달(개별 entry required 가 2차 게이트).
        type: 'subgroup',
        labelKey: 'sidebar.groups.system',
        icon: UserCog,
        required: ALL_ROLES,
        prefixes: ['/admin'],
        entries: [
          { to: '/admin/accounts', labelKey: 'sidebar.adminAccounts', icon: UserCog,     required: ['SUPER_ADMIN', 'OPS_ADMIN', 'AUDITOR'], indent: true },
          { to: '/admin/roles',    labelKey: 'sidebar.adminRoles',    icon: ShieldCheck, required: ['SUPER_ADMIN', 'OPS_ADMIN', 'AUDITOR'], indent: true },
          { to: '/admin/me',       labelKey: 'sidebar.myAccount',     icon: UserCircle,  required: ALL_ROLES, indent: true },
        ],
      },
    ],
  },
];

/** QA-509 즐겨찾는 메뉴 후보 — 사이드바의 이동 메뉴(하위 묶음 포함)를 한 줄로, 같은 주소는 한 번만. */
export interface FavoriteMenuCandidate {
  readonly to: string;
  readonly labelKey: string;
  readonly icon: LucideIcon;
  readonly required: ReadonlyArray<AdminRole>;
}

export const FAVORITE_MENU_CANDIDATES: ReadonlyArray<FavoriteMenuCandidate> = (() => {
  const seen = new Set<string>();
  const out: FavoriteMenuCandidate[] = [];
  const push = (e: NavEntry) => {
    if (seen.has(e.to)) return;
    seen.add(e.to);
    out.push({ to: e.to, labelKey: e.labelKey, icon: e.icon, required: e.required });
  };
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if ('type' in item && item.type === 'subgroup') item.entries.forEach(push);
      else push(item as NavEntry);
    }
  }
  return out;
})();

/** 메뉴 고르기 화면용 메뉴 한 줄 — 하위 묶음(예: 주문 관리 > 송장 일괄등록) 안이면 그 묶음 정보가 붙는다. */
export interface MenuPickEntry extends FavoriteMenuCandidate {
  /** 사이드바 하위 묶음의 labelKey·아이콘. 그룹 바로 아래 메뉴면 없음. */
  readonly subgroup?: { readonly labelKey: string; readonly icon: LucideIcon };
}

/**
 * QA-514 역할 권한 관리 · QA-509 즐겨찾는 메뉴 고르기 — 사이드바 그룹 순서대로 메뉴를 묶는다.
 * 하위 묶음 메뉴는 subgroup 이 붙어 화면에서 그룹 → 하위 묶음 → 메뉴 단계로 보여 줄 수 있다.
 */
export const PERMISSION_MENU_GROUPS: ReadonlyArray<{ headingKey: string; entries: ReadonlyArray<MenuPickEntry> }> =
  NAV_GROUPS.map((group) => ({
    headingKey: group.headingKey,
    entries: group.items.flatMap((item): MenuPickEntry[] =>
      'type' in item && item.type === 'subgroup'
        ? item.entries.map((e) => ({
            to: e.to,
            labelKey: e.labelKey,
            icon: e.icon,
            required: e.required,
            subgroup: { labelKey: item.labelKey, icon: item.icon },
          }))
        : [{ to: (item as NavEntry).to, labelKey: (item as NavEntry).labelKey, icon: (item as NavEntry).icon, required: (item as NavEntry).required }],
    ),
  })).filter((g) => g.entries.length > 0);

/** 그룹 안 메뉴를 화면 단계로 나눈다 — 그룹 바로 아래 메뉴 하나씩, 하위 묶음은 이어진 메뉴를 한 덩어리로. */
export type MenuPickBlock =
  | { readonly kind: 'entry'; readonly entry: MenuPickEntry }
  | { readonly kind: 'subgroup'; readonly labelKey: string; readonly icon: LucideIcon; readonly entries: ReadonlyArray<MenuPickEntry> };

export const toMenuPickBlocks = (entries: ReadonlyArray<MenuPickEntry>): MenuPickBlock[] => {
  const out: MenuPickBlock[] = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (!e.subgroup) out.push({ kind: 'entry', entry: e });
    else if (last && last.kind === 'subgroup' && last.labelKey === e.subgroup.labelKey) (last.entries as MenuPickEntry[]).push(e);
    else out.push({ kind: 'subgroup', labelKey: e.subgroup.labelKey, icon: e.subgroup.icon, entries: [e] });
  }
  return out;
};

interface NavLinkItemProps {
  entry: NavEntry;
}

const queryParam = (search: string, key: string): string | null =>
  new URLSearchParams(search).get(key);

const NavLinkItem = ({ entry }: NavLinkItemProps) => {
  const { t } = useTranslation('common');
  const Icon = entry.icon;
  const { isHydrating, currentAdmin } = useAuthContext();
  // QA-514 — 역할 권한(코드)과 역할별 접근 메뉴(최고관리자 설정) 둘 다 통과해야 보인다.
  const visible = useHasRole(entry.required) && isMenuAllowed(menusOf(currentAdmin), entry.to);
  const location = useLocation();

  /* Query-aware active for deep-link entries (e.g. /policies?type=TERMS) so only the
   * matching sub-category highlights instead of every variant sharing the pathname. */
  const resolveActive = (navIsActive: boolean): boolean => {
    if (!entry.matchSearch) return navIsActive;
    const [path, query] = entry.to.split('?');
    if (location.pathname !== path) return false;
    const wantType = query ? new URLSearchParams(query).get('type') : null;
    return queryParam(location.search, 'type') === wantType;
  };

  if (!isHydrating && !visible) return null;
  return (
    <NavLink
      to={entry.to}
      end={
        entry.to === '/dashboard' ||
        entry.to === '/products' ||
        entry.to === '/mileage' ||
        entry.to === '/cms/ever-tv' ||
        entry.to === '/orders' ||
        entry.to === '/payments' ||
        entry.to === '/notifications'
      }
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-2.5 rounded-md py-2 text-[13px] transition-colors',
          entry.indent ? 'pl-9 pr-3' : 'px-3',
          resolveActive(isActive)
            ? 'font-semibold'
            : 'font-medium text-[var(--content-default)] hover:bg-[var(--surface-1)] hover:text-[var(--content-strong)]',
        ].join(' ')
      }
      style={({ isActive }) =>
        resolveActive(isActive)
          ? {
              background: 'var(--sidebar-active-bg)',
              color: 'var(--sidebar-active-fg)',
            }
          : undefined
      }
    >
      {({ isActive }) => (
        <>
          {resolveActive(isActive) && !entry.indent ? (
            <span
              aria-hidden="true"
              className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r"
              style={{ background: 'var(--sidebar-active-bar)' }}
            />
          ) : null}
          <Icon
            size={15}
            aria-hidden="true"
            style={{ color: resolveActive(isActive) ? 'var(--sidebar-active-fg)' : 'var(--content-muted)' }}
          />
          <span>{t(entry.labelKey)}</span>
        </>
      )}
    </NavLink>
  );
};

interface NavSubGroupItemProps {
  subgroup: NavSubGroup;
  /** 검색 중일 때 사용자의 수동 접힘과 무관하게 강제 펼침. */
  forceOpen?: boolean;
}

const NavSubGroupItem = ({ subgroup, forceOpen = false }: NavSubGroupItemProps) => {
  const { t } = useTranslation('common');
  const Icon = subgroup.icon;
  const { isHydrating } = useAuthContext();
  const admin = useAuthContext().currentAdmin;
  const location = useLocation();

  const isAnyChildActive = subgroup.prefixes.some((prefix) =>
    location.pathname === prefix || location.pathname.startsWith(prefix + '/'),
  );

  /* subgroup defaults to expanded so child items are visible. Stays open until
   * user collapses it manually. 검색 중(forceOpen)에는 항상 펼침. */
  const [expanded, setExpanded] = useState(true);
  const open = forceOpen || expanded;
  void isAnyChildActive; // active state still drives icon/text colour below

  const menus = menusOf(admin);
  const hasAccess =
    isHydrating ||
    (subgroup.required.some((r) => admin?.roles.includes(r as AdminRole)) &&
      subgroup.entries.some((e) => isMenuAllowed(menus, e.to)));

  if (!isHydrating && !hasAccess) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={open}
        className={[
          'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-[13px] transition-colors',
          isAnyChildActive
            ? 'font-semibold'
            : 'font-medium text-[var(--content-default)] hover:bg-[var(--surface-1)] hover:text-[var(--content-strong)]',
        ].join(' ')}
        style={isAnyChildActive ? { color: 'var(--sidebar-active-fg)' } : undefined}
      >
        <Icon
          size={15}
          aria-hidden="true"
          style={{
            color: isAnyChildActive ? 'var(--sidebar-active-fg)' : 'var(--content-muted)',
          }}
        />
        <span className="flex-1 text-left">{t(subgroup.labelKey)}</span>
        <motion.span
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ display: 'inline-flex', color: 'var(--content-muted)' }}
        >
          <ChevronDown size={12} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="space-y-0.5 pb-1">
              {subgroup.entries.map((entry) => (
                <NavLinkItem key={entry.to} entry={entry} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const isSubGroup = (item: NavItem): item is NavSubGroup =>
  (item as NavSubGroup).type === 'subgroup';

interface NavGroupSectionProps {
  group: NavGroup;
  expanded: boolean;
  onToggle: () => void;
  /** 검색 중일 때 하위 서브그룹까지 강제 펼침. */
  forceExpandSubgroups?: boolean;
}

const NavGroupSection = ({ group, expanded, onToggle, forceExpandSubgroups = false }: NavGroupSectionProps) => {
  const { t } = useTranslation('common');
  const { isHydrating } = useAuthContext();
  const admin = useAuthContext().currentAdmin;

  const allEntries = group.items.flatMap((item) =>
    isSubGroup(item) ? item.entries : [item],
  );
  const hasAnyVisible =
    isHydrating ||
    allEntries.some(
      (e) => e.required.some((r) => admin?.roles.includes(r as AdminRole)) && isMenuAllowed(menusOf(admin), e.to),
    );
  if (!hasAnyVisible) return null;

  return (
    <div className="mb-3">
      <button
        className="mb-1 flex w-full items-center justify-between px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors hover:text-[var(--content-default)]"
        style={{ color: 'var(--content-muted)' }}
        onClick={onToggle}
      >
        <span>{t(group.headingKey)}</span>
        <motion.span
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ display: 'inline-flex' }}
        >
          <ChevronDown size={10} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="space-y-0.5 pb-1">
              {group.items.map((item) =>
                isSubGroup(item) ? (
                  <NavSubGroupItem key={item.labelKey} subgroup={item} forceOpen={forceExpandSubgroups} />
                ) : (
                  <NavLinkItem key={item.to} entry={item} />
                ),
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface SidebarProps {
  /** 200(b) — 모바일(<md) 오프캔버스 드로어 열림 상태. md+ 에서는 무시(상시 노출). */
  readonly mobileOpen?: boolean;
  /** 200(b) — 모바일 드로어에서 항목 이동 시 드로어를 닫기 위한 콜백(데스크톱 무관). */
  readonly onNavigate?: () => void;
}

export const Sidebar = ({ mobileOpen = false, onNavigate }: SidebarProps = {}) => {
  const { t } = useTranslation('common');
  const { currentAdmin, logout } = useAuthContext();
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    'sidebar.groups.main': true,
    'sidebar.groups.products': true,
    'sidebar.groups.partnerships': true,
    'sidebar.groups.promotion': true,
    'sidebar.groups.content': true,
    'sidebar.groups.orders': true,
    'sidebar.groups.members': true,
    'sidebar.groups.notifications': true,
    'sidebar.groups.settings': true,
  });

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* 메뉴 검색 — 정적 NAV_GROUPS 를 번역된 라벨 기준으로 클라이언트 필터.
   * - 일반 항목: 라벨 매치 시 노출.
   * - 서브그룹: 서브그룹명 매치 시 전체, 아니면 매치되는 자식만 남김.
   * - 매치 자식이 없는 그룹/서브그룹은 숨김.
   * RBAC 가시성은 NavLinkItem/useHasRole 이 2차 게이트로 그대로 적용된다. */
  const query = search.trim().toLowerCase();
  const visibleGroups = useMemo<ReadonlyArray<NavGroup>>(() => {
    if (!query) return NAV_GROUPS;
    const matches = (labelKey: string) => t(labelKey).toLowerCase().includes(query);
    return NAV_GROUPS.flatMap((group) => {
      const items = group.items.flatMap((item): NavItem[] => {
        if (isSubGroup(item)) {
          if (matches(item.labelKey)) return [item];
          const entries = item.entries.filter((e) => matches(e.labelKey));
          return entries.length ? [{ ...item, entries }] : [];
        }
        return matches(item.labelKey) ? [item] : [];
      });
      return items.length ? [{ ...group, items }] : [];
    });
  }, [query, t]);

  const initials = currentAdmin?.name
    ? currentAdmin.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <aside
      /* 200(b) — 모바일(<md): 좌측 오프캔버스 드로어(fixed, mobileOpen 시 translate-x-0).
         데스크톱(md+): 기존과 동일하게 sticky 인-플로우 사이드바(translate 무효화 + transition 제거).
         ※ md+ 에서의 표시는 변경 전과 byte-identical(sticky top-0 flex h-dvh w-[248px] flex-shrink-0
         flex-col border-r). 모바일 전용으로만 fixed/translate/transition 이 적용된다. */
      className={[
        'fixed inset-y-0 left-0 z-50 flex h-dvh w-[248px] flex-shrink-0 flex-col border-r',
        'transition-transform duration-200 ease-out',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        // md+: 원래대로 sticky 인-플로우(top-0). 모바일 전용 fixed/inset/translate/transition 무효화
        // → 데스크톱 표시는 변경 전과 동일(sticky top-0 flex h-dvh w-[248px] flex-shrink-0 flex-col border-r).
        'md:sticky md:inset-y-auto md:left-auto md:top-0 md:z-auto md:translate-x-0 md:transition-none',
      ].join(' ')}
      style={{ background: 'var(--surface-0)', borderColor: 'var(--divider-strong)' }}
      aria-label={t('sidebar.label', 'primary navigation')}
    >
      {/* Brand — QA-508 누르면 백오피스 메인("/" → 라우터가 대시보드로). 모바일 드로어는 이동하며 닫는다. */}
      <Link
        to="/"
        aria-label="백오피스 메인"
        onClick={() => onNavigate?.()}
        className="flex items-center gap-2.5 px-5 no-underline outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
        style={{ height: '60px', borderBottom: '1px solid var(--divider-strong)' }}
        data-testid="sidebar-brand"
      >
        <div
          className="flex h-7 w-7 items-center justify-center rounded-md text-[12px] font-bold text-white"
          style={{ background: 'var(--accent-default)' }}
          aria-hidden="true"
        >
          E
        </div>
        <div className="flex flex-col leading-tight">
          <p className="text-[14px] font-semibold tracking-tight" style={{ color: 'var(--content-strong)' }}>
            Everline
          </p>
          <p className="text-[10px] uppercase tracking-[0.12em]" style={{ color: 'var(--content-muted)' }}>
            Admin
          </p>
        </div>
      </Link>

      {/* Search */}
      <div className="px-3 pt-4 pb-3">
        <div className="relative">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--content-muted)' }}
          />
          <input
            type="search"
            placeholder={t('sidebar.search', '메뉴 검색...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md py-1.5 pl-7 pr-2.5 text-[12px] outline-none transition-colors focus:[box-shadow:var(--focus-ring-brand)]"
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--divider-strong)',
              color: 'var(--content-default)',
            }}
          />
        </div>
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1 px-3 py-1">
        {/* 200(b) — 모바일 드로어: nav 내부 링크(<a>) 클릭 시 드로어를 닫는다(이벤트 위임).
            그룹 토글 버튼 등 링크가 아닌 클릭은 무시. NavLink active 로직은 건드리지 않는다. */}
        <nav
          onClick={(e) => {
            if (onNavigate && (e.target as HTMLElement).closest('a')) onNavigate();
          }}
        >
          {visibleGroups.length === 0 ? (
            <p
              className="px-3 py-6 text-center text-[12px]"
              style={{ color: 'var(--content-muted)' }}
            >
              {t('sidebar.searchEmpty', '검색 결과가 없습니다')}
            </p>
          ) : (
            visibleGroups.map((group) => (
              <NavGroupSection
                key={group.headingKey}
                group={group}
                expanded={query ? true : (expandedGroups[group.headingKey] ?? true)}
                onToggle={() => toggleGroup(group.headingKey)}
                forceExpandSubgroups={!!query}
              />
            ))
          )}
        </nav>
      </ScrollArea>

      {/* Bottom user section */}
      <div
        className="px-3 py-3"
        style={{ borderTop: '1px solid var(--divider-strong)' }}
      >
        {currentAdmin ? (
          <div className="mb-2 flex items-center gap-2.5 px-2 py-1.5">
            <div
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
              style={{ background: 'var(--accent-default)' }}
              aria-hidden="true"
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                {currentAdmin.name}
              </p>
              <p className="truncate text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {currentAdmin.roles[0] ?? 'Admin'}
              </p>
            </div>
          </div>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start rounded-md text-[12px]"
          style={{ color: 'var(--content-muted)' }}
          onClick={() => void logout()}
        >
          <LogOut size={13} aria-hidden="true" />
          <span>{t('sidebar.logout')}</span>
        </Button>
      </div>
    </aside>
  );
};
